/**
 * Letting the people who joined actually see the album.
 *
 * Joining a group writes a membership row and nothing else. It cannot write the
 * one thing that grants access — a copy of the group key sealed to the new
 * member's public half — because only somebody already holding the key can
 * produce that, and the server holds nothing.
 *
 * So the handover happens here, in the browser of whoever opens the group next.
 * It is deliberately silent and unconditional: being invited was the decision,
 * and asking a member to confirm it a second time would be ceremony that leaves
 * people staring at an empty album until somebody notices a notification.
 *
 * Cheap in the common case — the query returns an empty list and nothing else
 * runs — and it retries on every group open, so a failure fixes itself.
 */

import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sealGroupKeyFor } from "./keyring.js";
import { trpcClient } from "./trpc.js";

export function useKeyHandover(groupId: string, ready: boolean): void {
  const queryClient = useQueryClient();

  const pending = useQuery({
    queryKey: ["pendingKeyGrants", groupId],
    // Only somebody holding the key can help, so there is nothing to ask about
    // until the keyring is open.
    enabled: ready && Boolean(groupId),
    queryFn: () => trpcClient.groups.pendingKeyGrants.query({ groupId }),
    // Members arrive rarely, and a minute late is not late.
    staleTime: 60_000,
    retry: false,
  });

  const waiting = pending.data?.members;

  useEffect(() => {
    if (!ready || !waiting || waiting.length === 0) return;

    let current = true;
    void (async () => {
      for (const member of waiting) {
        if (!current) return;
        try {
          const wrappedGroupKey = await sealGroupKeyFor(
            groupId,
            member.publicKey,
          );
          await trpcClient.groups.grantKeyTo.mutate({
            groupId,
            userId: member.userId,
            wrappedGroupKey,
          });
        } catch {
          /*
           * One bad public key must not stop the rest. Whoever fails here is
           * simply still waiting, and the next member to open the album tries
           * again — which is the same recovery path as nobody having been
           * online at all.
           */
        }
      }

      if (current) {
        await queryClient.invalidateQueries({
          queryKey: ["pendingKeyGrants", groupId],
        });
      }
    })();

    return () => {
      current = false;
    };
  }, [ready, waiting, groupId, queryClient]);
}
